import React, { useState } from "react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtDate } from "@/lib/helpers";
import {
  downloadDocument, previewDocument, humanSize, isImage, visibilityLabel, visibilityClass,
} from "@/lib/documents";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Download, Eye, Trash2 } from "lucide-react";

function DocIcon({ contentType }) {
  const Icon = isImage(contentType) ? ImageIcon : FileText;
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-navy/5 text-navy">
      <Icon className="h-5 w-5" strokeWidth={1.75} />
    </div>
  );
}

export default function DocumentList({ documents, onChanged, readOnly = false, showCase = false }) {
  const [toDelete, setToDelete] = useState(null);

  const confirmDelete = async () => {
    try {
      await api.delete(`/documents/${toDelete.id}`);
      toast.success("Document removed");
      onChanged?.();
    } catch {
      toast.error("Unable to remove the document.");
    } finally {
      setToDelete(null);
    }
  };

  if (!documents || documents.length === 0) {
    return (
      <Card className="border-dashed border-border py-12 text-center shadow-none">
        <p className="text-sm text-muted-foreground">No documents yet.</p>
      </Card>
    );
  }

  return (
    <>
      <Card className="divide-y divide-border border-border shadow-sm">
        {documents.map((d) => (
          <div key={d.id} data-testid={`document-row-${d.id}`} className="flex items-center gap-4 px-4 py-3">
            <DocIcon contentType={d.content_type} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-navy">{d.original_filename}</p>
              <p className="truncate text-xs text-muted-foreground">
                {d.category}
                {showCase && d.case_title ? ` · ${d.case_title}` : ""}
                {" · "}{humanSize(d.size)} · {d.uploaded_by ? `${d.uploaded_by} · ` : ""}{fmtDate(d.created_at)}
              </p>
            </div>
            <span className={cn("hidden shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium sm:inline-flex", visibilityClass(d.visibility))}>
              {visibilityLabel(d.visibility)}
            </span>
            <div className="flex shrink-0 items-center gap-1">
              <button onClick={() => previewDocument(d)} data-testid={`doc-preview-${d.id}`} title="Preview"
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-navy">
                <Eye className="h-4 w-4" />
              </button>
              <button onClick={() => downloadDocument(d)} data-testid={`doc-download-${d.id}`} title="Download"
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-navy">
                <Download className="h-4 w-4" />
              </button>
              {!readOnly && (
                <button onClick={() => setToDelete(d)} data-testid={`doc-delete-${d.id}`} title="Delete"
                  className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-rose-50 hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove document?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.original_filename}" will be removed from this case. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="doc-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="doc-delete-confirm" className="bg-rose-600 hover:bg-rose-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
